import { useEffect, useState, useCallback, useRef } from 'react';
import { IconInfo, IconWithTooltip } from '@payright/web-components';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { media, IconGreenCheckIco } from '@payright/web-components';
import { ControlledInputField, ControlledDropdown, ControlledDatePicker } from '../form-fields';
import type { CustomerDetailsData as BnplCheckoutApiCustomerDetailsData } from '@payright/types-bnpl-checkout-api';
import { calculateAgeLuxon, envIsTrue } from './../../util/helpers';
import getConstants from './../../util/constants';

const {
  CUSTOMER_TITLES,
  DOB_YEARS,
  MOBILE_VALIDATION_REGEX,
  MOBILE_NUMBER_FORMAT_VALIDATION,
  NONEDITABLE_FIELD_MESSAGE,
} = getConstants();

export type CustomerDetailsData = BnplCheckoutApiCustomerDetailsData;

export type CustomerDetailsProps = {
  existingCustomer: boolean;
  readOnly: boolean;
};

const SCCustomerDetails = styled.div`
  .details-body {
    display: grid;
    grid-template-columns: auto;
    gap: 1.5rem;
  }

  .names-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    align-items: normal;
  }

  .label-with-tooltip {
    display: inline-flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;
  }

  .password-fields {
    display: grid;
    gap: 1.5rem;
  }

  .password-messages {
    font-size: 0.86em;
    margin-top: -1.5em;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  ${media.min.medium} {
    .details-body {
      grid-template-columns: repeat(2, minmax(10px, 1fr));
    }

    .names-wrapper {
      flex-direction: row;
      gap: 0.5rem;
      align-items: flex-end;
    }

    .password-fields {
      grid-column: span 2;
      grid-auto-flow: column;
      grid-template-columns: repeat(2, minmax(10px, 1fr));
      grid-template-rows: repeat(2, minmax(10px, 1fr));
    }

    .password-messages {
      grid-column: span 2;
    }
  }
`;

export const CustomerDetails = ({ readOnly, existingCustomer }: CustomerDetailsProps) => {
  const [liStyle1, setLiStyle1] = useState({ color: 'red' });
  const [liStyle2, setLiStyle2] = useState({ color: 'red' });
  const [liStyle3, setLiStyle3] = useState({ color: 'red' });
  const [liStyle4, setLiStyle4] = useState({ color: 'red' });

  const { watch, errors } = useFormContext<{
    customerDetails: CustomerDetailsData;
  }>();

  const password: string = watch('customerDetails.password');

  const passwordCheck = useCallback(() => {
    const hasMin8Chars = new RegExp('^(?=.{8,})');
    const hasUppercase = new RegExp('^(?=.*[A-Z])');
    const hasLowercase = new RegExp('^(?=.*[a-z])');
    const hasSpecialChar = new RegExp('^(?=.*[!@#$%^&*])');

    if (hasMin8Chars.test(password)) {
      setLiStyle1({ color: 'green' });
    } else {
      setLiStyle1({ color: 'red' });
    }

    if (hasUppercase.test(password)) {
      setLiStyle2({ color: 'green' });
    } else {
      setLiStyle2({ color: 'red' });
    }

    if (hasLowercase.test(password)) {
      setLiStyle3({ color: 'green' });
    } else {
      setLiStyle3({ color: 'red' });
    }

    if (hasSpecialChar.test(password)) {
      setLiStyle4({ color: 'green' });
    } else {
      setLiStyle4({ color: 'red' });
    }
  }, [password]);

  const sectionRef = useRef<HTMLDivElement>(null);

  // Scroll to block (by ID) when user submits and validation fails
  useEffect(() => {
    if (Object.keys(errors).length > 0 && errors.customerDetails && sectionRef.current) {
      // check for null and focus target on block with errors
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [errors]); // Errors object changes on submit

  useEffect(() => {
    passwordCheck();
  }, [passwordCheck]);

  return (
    <SCCustomerDetails>
      <div ref={sectionRef} style={{ position: 'relative', top: '-2em' }} />
      <div className="details-body">
        <div className="names-wrapper">
          <ControlledDropdown
            name="customerDetails.title"
            rules={{ required: 'Required' }}
            options={CUSTOMER_TITLES}
            readOnly={readOnly}
            maxWidth="22%"
            error={errors.customerDetails?.title?.message}
          >
            Title
          </ControlledDropdown>
          <ControlledInputField
            name="customerDetails.firstName"
            rules={{
              required: 'First name is required',
              pattern: {
                value: /[A-Za-z]/i,
                message: 'Enter a valid First Name',
              },
            }}
            readOnly={readOnly}
            error={errors.customerDetails?.firstName?.message}
          >
            First Name
          </ControlledInputField>
          <ControlledInputField
            name="customerDetails.middleName"
            rules={{
              pattern: {
                value: /[A-Za-z]/i,
                message: 'Enter a valid middle name',
              },
            }}
            readOnly={readOnly}
            error={errors.customerDetails?.middleName?.message}
          >
            Middle Name (optional)
          </ControlledInputField>
        </div>
        <div className="names-wrapper">
          <ControlledInputField
            name="customerDetails.lastName"
            rules={{
              required: 'Last name is required',
              pattern: {
                value: /[A-Za-z]/i,
                message: 'Enter a valid last name',
              },
            }}
            readOnly={readOnly}
            error={errors.customerDetails?.lastName?.message}
          >
            Last Name
          </ControlledInputField>
        </div>
        <ControlledInputField
          name="customerDetails.email"
          rules={{
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i,
              message: 'Enter a valid email address',
            },
          }}
          readOnly={readOnly}
          error={errors.customerDetails?.email?.message}
        >
          Email
        </ControlledInputField>
        {!readOnly && (
          <ControlledInputField
            name="customerDetails.confirmEmail"
            rules={{
              required: 'Email confirmation is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i,
                message: 'Enter a valid email address',
              },
              validate: (value: any) =>
                value === watch('customerDetails.email') || 'Email needs to match',
            }}
            readOnly={readOnly}
            error={errors.customerDetails?.confirmEmail?.message}
          >
            Confirm Email
          </ControlledInputField>
        )}
        <ControlledInputField
          name="customerDetails.phone"
          rules={{
            required: 'Phone number is required',
            pattern: envIsTrue('REACT_APP_SKIP_CUSTOMER_MOBILE_VALIDATION')
              ? undefined
              : {
                  value: MOBILE_VALIDATION_REGEX,
                  message: MOBILE_NUMBER_FORMAT_VALIDATION,
                },
          }}
          readOnly={existingCustomer}
          error={errors.customerDetails?.phone?.message}
        >
          <span className="label-with-tooltip">
            Mobile No.
            {!readOnly && (
              <IconWithTooltip content={NONEDITABLE_FIELD_MESSAGE}>
                <IconInfo />
              </IconWithTooltip>
            )}
          </span>
        </ControlledInputField>
        <ControlledDatePicker
          name="customerDetails.dateOfBirth"
          rules={{
            required: 'Date of Birth is required',
            validate: (value: any) => calculateAgeLuxon(value) || 'Must be at least 18 years old',
          }}
          readOnly={readOnly}
          startYear={new Date().getFullYear() - 18}
          endYear={new Date().getFullYear() - 18 - DOB_YEARS}
          error={errors.customerDetails?.dateOfBirth?.message}
        >
          Date of Birth
        </ControlledDatePicker>
        {/* If the customer record is not in SugarCRM yet (i.e not an existing customer) ,
         * the user will be prompted to enter their password as well. */}
        {!existingCustomer && (
          <div className="password-fields">
            <ControlledInputField
              name="customerDetails.password"
              placeholder=""
              type="password"
              rules={{
                required: 'Password is required',
                minLength: 8,
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*[\]"';:_\-<>., =+/\\]).{8,}$/,
                  message: 'Password strength does not meet the requirements',
                },
              }}
              readOnly={existingCustomer}
              error={errors.customerDetails?.password?.message}
            >
              Password *
            </ControlledInputField>
            <ul className="password-messages">
              <li style={liStyle1}>
                {liStyle1.color === 'green' ? <IconGreenCheckIco /> : '- '}Minimum 8 characters
              </li>
              <li style={liStyle2}>
                {liStyle2.color === 'green' ? <IconGreenCheckIco /> : '- '}1 upper case letter
              </li>
              <li style={liStyle3}>
                {liStyle3.color === 'green' ? <IconGreenCheckIco /> : '- '}1 lower case letter
              </li>
              <li style={liStyle4}>
                {liStyle4.color === 'green' ? <IconGreenCheckIco /> : '- '}1 special character
              </li>
            </ul>
            <ControlledInputField
              name="customerDetails.confirmPassword"
              placeholder=""
              type="password"
              rules={{
                required: 'Password confirmation is required',
                validate: (value: any) =>
                  value === watch('customerDetails.password') || 'Password needs to match',
              }}
              readOnly={existingCustomer}
              error={errors.customerDetails?.confirmPassword?.message}
            >
              Confirm Password *
            </ControlledInputField>
          </div>
        )}
      </div>
    </SCCustomerDetails>
  );
};
